import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSidebar } from './ui/sidebar';
import { useRightSidebar } from '../contexts/RightSidebarContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
} from './ui/accordion';
import { ChevronDown, GripVertical, X } from 'lucide-react';
import {
  FLOATING_EDITOR_CONTROL_MOBILE_LEFT,
  floatingEditorControlTop,
} from '../constants/layout';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';

const PROJECT_SIDEBAR_COLLAPSE_BREAKPOINT = 1200;
const DEFAULT_TOC_WIDTH_PX = 280;
const MIN_TOC_WIDTH_PX = 200;
const MAX_TOC_WIDTH_PX = 480;
const TOC_PINNED_WIDTH_COOKIE = 'toc_pinned_width';
const TOC_WIDTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

const readStoredTocWidth = (): number => {
  if (typeof document === 'undefined') {
    return DEFAULT_TOC_WIDTH_PX;
  }
  const cookies = document.cookie.split(';');
  const cookie = cookies.find((entry) => entry.trim().startsWith(`${TOC_PINNED_WIDTH_COOKIE}=`));
  if (!cookie) {
    return DEFAULT_TOC_WIDTH_PX;
  }
  const value = parseInt(cookie.split('=')[1], 10);
  if (Number.isNaN(value) || value < MIN_TOC_WIDTH_PX || value > MAX_TOC_WIDTH_PX) {
    return DEFAULT_TOC_WIDTH_PX;
  }
  return value;
};

const clampTocWidth = (width: number): number =>
  Math.max(MIN_TOC_WIDTH_PX, Math.min(MAX_TOC_WIDTH_PX, width));


interface TocNode {
  heading: { level: number; text: string; pos: number };
  children: TocNode[];
  paragraphs: Array<{ text: string; pos: number }>;
  originalIndex: number;
}

const tocItemId = (originalIndex: number): string => `heading-${originalIndex}`;

const collectDefaultOpenTocItemIds = (nodes: TocNode[]): string[] => {
  const ids: string[] = [];
  const walk = (current: TocNode[]): void => {
    current.forEach((node) => {
      if (node.children.length > 0 && node.paragraphs.length === 0) {
        ids.push(tocItemId(node.originalIndex));
      }
      walk(node.children);
    });
  };
  walk(nodes);
  return ids;
};

const collectAncestorTocItemIdsForPos = (nodes: TocNode[], activePos: number): string[] => {
  const walk = (current: TocNode[], ancestors: string[]): string[] | null => {
    for (const node of current) {
      const itemId = tocItemId(node.originalIndex);
      const nextAncestors = [...ancestors, itemId];
      if (node.heading.pos === activePos) {
        return ancestors;
      }
      if (node.paragraphs.some((paragraph) => paragraph.pos === activePos)) {
        return nextAncestors;
      }
      const nested = walk(node.children, nextAncestors);
      if (nested) {
        return nested;
      }
    }
    return null;
  };

  return walk(nodes, []) ?? [];
};

const collectAncestorHeadingPositionsForPos = (nodes: TocNode[], activePos: number): number[] => {
  const walk = (current: TocNode[], ancestors: number[]): number[] | null => {
    for (const node of current) {
      const nextAncestors = [...ancestors, node.heading.pos];
      if (node.heading.pos === activePos) {
        return ancestors;
      }
      if (node.paragraphs.some((paragraph) => paragraph.pos === activePos)) {
        return nextAncestors;
      }
      const nested = walk(node.children, nextAncestors);
      if (nested) {
        return nested;
      }
    }
    return null;
  };

  return walk(nodes, []) ?? [];
};

const ACTIVE_TOC_ITEM_STYLE: React.CSSProperties = {
  color: 'var(--scribane-green)',
  fontWeight: 500,
  backgroundColor: 'rgba(94, 204, 128, 0.16)',
};

const ACTIVE_TOC_ANCESTOR_TEXT_STYLE: React.CSSProperties = {
  color: 'var(--scribane-green)',
  fontWeight: 500,
};

interface FloatingTocProps {
  tocHeadings: Array<{ level: number; text: string; pos: number; kind?: 'heading' | 'scene' }>;
  tocParagraphs: Array<{ text: string; pos: number }>;
  isFloatingTocVisible: boolean;
  setIsFloatingTocVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isTocPinned: boolean;
  setIsTocPinned: React.Dispatch<React.SetStateAction<boolean>>;
  activeTocPos?: number | null;
  isMobile: boolean;
  isReadOnly: boolean;
  isFocusMode?: boolean;
  scrollToTocPosition: (pos: number) => void;
  moveTocSection: (fromIndex: number, toIndex: number) => void;
  draggedTocIndex: number | null;
  setDraggedTocIndex: React.Dispatch<React.SetStateAction<number | null>>;
  dragOverTocIndex: number | null;
  setDragOverTocIndex: React.Dispatch<React.SetStateAction<number | null>>;
  touchDraggedIndex: number | null;
  setTouchDraggedIndex: React.Dispatch<React.SetStateAction<number | null>>;
  touchDragOverIndex: number | null;
  setTouchDragOverIndex: React.Dispatch<React.SetStateAction<number | null>>;
  touchStartY: number | null;
  setTouchStartY: React.Dispatch<React.SetStateAction<number | null>>;
  tocListRef: React.RefObject<HTMLUListElement>;
  t: (key: string) => string;
}

export const FloatingToc: React.FC<FloatingTocProps> = React.memo(({
  tocHeadings,
  tocParagraphs,
  isFloatingTocVisible,
  setIsFloatingTocVisible,
  isTocPinned,
  setIsTocPinned,
  activeTocPos = null,
  isMobile,
  isReadOnly,
  isFocusMode = false,
  scrollToTocPosition,
  moveTocSection,
  draggedTocIndex,
  setDraggedTocIndex,
  dragOverTocIndex,
  setDragOverTocIndex,
  touchDraggedIndex,
  setTouchDraggedIndex,
  touchDragOverIndex,
  setTouchDragOverIndex,
  touchStartY,
  setTouchStartY,
  tocListRef,
  t,
}) => {
  let sidebar;
  try {
    sidebar = useSidebar();
  } catch {
    sidebar = null;
  }

  let rightSidebar;
  try {
    rightSidebar = useRightSidebar();
  } catch {
    rightSidebar = null;
  }
  const isRightSidebarOpen = rightSidebar?.open ?? false;
  const isDesktopPinned = !isMobile && isTocPinned;
  const isDesktopFloating = !isMobile && !isTocPinned;
  const isOverlayVisible = isMobile
    ? (isFloatingTocVisible || isTocPinned)
    : (isDesktopFloating && isFloatingTocVisible);
  const desktopFloatingLeft = isFocusMode ? '1rem' : (sidebar?.open ? '17rem' : '4rem');
  const [pinnedWidthPx, setPinnedWidthPx] = useState<number>(readStoredTocWidth);
  const [isResizingPinnedWidth, setIsResizingPinnedWidth] = useState<boolean>(false);
  const pinnedPanelRef = useRef<HTMLDivElement | null>(null);
  const pinnedWidthRef = useRef<number>(pinnedWidthPx);
  pinnedWidthRef.current = pinnedWidthPx;

  useEffect(() => {
    if (!sidebar || !isDesktopPinned) return;

    const collapseIfNeeded = () => {
      if (!sidebar!.open) return;
      if (isRightSidebarOpen || window.innerWidth < PROJECT_SIDEBAR_COLLAPSE_BREAKPOINT) {
        sidebar!.setOpen(false);
      }
    };

    collapseIfNeeded();
    window.addEventListener('resize', collapseIfNeeded);
    return () => window.removeEventListener('resize', collapseIfNeeded);
  }, [isDesktopPinned, sidebar, isRightSidebarOpen]);

  useEffect(() => {
    if (!isResizingPinnedWidth) {
      return;
    }

    const handleMouseMove = (event: MouseEvent): void => {
      const panelLeft = pinnedPanelRef.current?.getBoundingClientRect().left ?? 0;
      setPinnedWidthPx(clampTocWidth(event.clientX - panelLeft));
    };

    const handleMouseUp = (): void => {
      setIsResizingPinnedWidth(false);
      if (typeof document !== 'undefined') {
        document.cookie = `${TOC_PINNED_WIDTH_COOKIE}=${pinnedWidthRef.current}; path=/; max-age=${TOC_WIDTH_COOKIE_MAX_AGE}`;
      }
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingPinnedWidth]);

  const startPinnedWidthResize = (event: React.MouseEvent): void => {
    event.preventDefault();
    setIsResizingPinnedWidth(true);
  };

  const tree = useMemo(() => {
    const treeResult: TocNode[] = [];
    const stack: TocNode[] = [];
    let originalIndex = 0;

    tocHeadings.forEach((heading) => {
      const node: TocNode = {
        heading,
        children: [],
        paragraphs: [],
        originalIndex: originalIndex++,
      };

      while (stack.length > 0 && stack[stack.length - 1].heading.level >= heading.level) {
        stack.pop();
      }

      if (stack.length === 0) {
        treeResult.push(node);
      } else {
        const parent = stack[stack.length - 1];
        parent.children.push(node);
      }

      stack.push(node);
    });

    const sortedParagraphs = [...tocParagraphs].sort((a, b) => a.pos - b.pos);
    const allNodes: TocNode[] = [];
    const collectNodes = (nodes: TocNode[]) => {
      nodes.forEach((node) => {
        allNodes.push(node);
        collectNodes(node.children);
      });
    };
    collectNodes(treeResult);
    const sortedNodes = [...allNodes].sort((a, b) => a.heading.pos - b.heading.pos);

    sortedParagraphs.forEach((paragraph) => {
      for (let i = sortedNodes.length - 1; i >= 0; i--) {
        if (paragraph.pos > sortedNodes[i].heading.pos) {
          const nextNode = i < sortedNodes.length - 1 ? sortedNodes[i + 1] : null;
          if (!nextNode || paragraph.pos < nextNode.heading.pos) {
            sortedNodes[i].paragraphs.push(paragraph);
            break;
          }
        }
      }
    });

    return treeResult;
  }, [tocHeadings, tocParagraphs]);

  const defaultOpenIdsKey = useMemo(
    () => collectDefaultOpenTocItemIds(tree).join(','),
    [tree],
  );
  const [openIds, setOpenIds] = useState<string[]>(() => (
    defaultOpenIdsKey ? defaultOpenIdsKey.split(',') : []
  ));
  const ancestorHeadingPositions = useMemo(() => {
    if (activeTocPos === null) {
      return new Set<number>();
    }
    return new Set(collectAncestorHeadingPositionsForPos(tree, activeTocPos));
  }, [tree, activeTocPos]);

  useEffect(() => {
    setOpenIds(defaultOpenIdsKey ? defaultOpenIdsKey.split(',') : []);
  }, [defaultOpenIdsKey]);

  const tocScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollToActiveRef = useRef(false);
  const isTocPanelVisible = isOverlayVisible || isDesktopPinned;

  useEffect(() => {
    if (isTocPanelVisible) {
      shouldScrollToActiveRef.current = true;
    }
  }, [isTocPanelVisible]);

  useEffect(() => {
    if (!isTocPanelVisible || activeTocPos === null || !shouldScrollToActiveRef.current) {
      return;
    }

    const ancestorIds = collectAncestorTocItemIdsForPos(tree, activeTocPos);
    if (ancestorIds.length > 0) {
      setOpenIds((current) => Array.from(new Set([...current, ...ancestorIds])));
    }

    const timeoutId = window.setTimeout(() => {
      const activeItem = tocScrollContainerRef.current?.querySelector(
        `[data-toc-item-pos="${activeTocPos}"]`,
      );
      if (activeItem instanceof HTMLElement) {
        activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      shouldScrollToActiveRef.current = false;
    }, 50);

    return () => window.clearTimeout(timeoutId);
  }, [isTocPanelVisible, activeTocPos, tree]);

  const setTocItemOpen = (itemId: string, isOpen: boolean): void => {
    setOpenIds((current) => {
      const next = new Set(current);
      if (isOpen) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return [...next];
    });
  };

  const flatIndexMap = useMemo(() => {
    const headingsWithType = tocHeadings.map((h, idx) => ({ ...h, originalIndex: idx, type: 'heading' as const }));
    const paragraphsWithType = tocParagraphs.map((p, idx) => ({ ...p, originalIndex: idx, type: 'paragraph' as const, level: 0 }));
    const combined = [...headingsWithType, ...paragraphsWithType].sort((a, b) => a.pos - b.pos);
    const map = new Map<string, number>();
    combined.forEach((item, index) => {
      map.set(`${item.pos}-${item.type}`, index);
    });
    return map;
  }, [tocHeadings, tocParagraphs]);

  const getFlatIndex = (pos: number, type: 'heading' | 'paragraph'): number => {
    return flatIndexMap.get(`${pos}-${type}`) ?? -1;
  };

  // Combined heading+paragraph list, sorted by document position, carrying each
  // item's heading level (null for paragraphs). Index i here always matches
  // flatIndex i from flatIndexMap, since both are built from the same sort.
  const combinedSorted = useMemo(() => {
    const headingsWithType = tocHeadings.map((h) => ({ pos: h.pos, level: h.level, type: 'heading' as const }));
    const paragraphsWithType = tocParagraphs.map((p) => ({ pos: p.pos, level: null as number | null, type: 'paragraph' as const }));
    return [...headingsWithType, ...paragraphsWithType].sort((a, b) => a.pos - b.pos);
  }, [tocHeadings, tocParagraphs]);

  // Finds the heading that would contain the dragged item if it landed at
  // targetFlatIndex: the nearest heading at or before that position whose level
  // is shallower than the dragged item's (or any heading, for a dragged
  // paragraph, which always attaches to the nearest preceding heading).
  // Starting "at" targetFlatIndex (inclusive) means hovering directly on a
  // qualifying parent heading highlights that heading itself, not its own parent.
  const findWouldBeParentFlatIndex = (targetFlatIndex: number, draggedLevel: number | null): number | null => {
    for (let i = targetFlatIndex; i >= 0; i--) {
      const item = combinedSorted[i];
      if (item && item.type === 'heading' && (draggedLevel === null || item.level < draggedLevel)) {
        return i;
      }
    }
    return null;
  };

  const nestingIndentRem = 1;

  const renderDragHandle = (
    flatIndex: number,
    isDraggable: boolean,
    onDragStartIndex: (index: number) => void,
    onTouchStartIndex: (index: number, clientY: number) => void
  ) => {
    if (isReadOnly) return null;
    return (
      <ButtonTooltip tooltip={t('toc_drag_section_tooltip')} side="left" hidden={isMobile}>
        <span
          draggable={isDraggable && !isMobile}
          className={(isMobile ? 'flex' : 'hidden group-hover:flex') + ' hover:bg-accent transition-colors'}
          style={{
            cursor: isMobile ? 'pointer' : 'grab',
            padding: '0.25rem',
            marginLeft: '0.25rem',
            color: 'var(--muted-foreground)',
            alignItems: 'center',
            flexShrink: 0,
            borderRadius: '0.375rem',
            touchAction: 'none',
            userSelect: 'none',
          }}
          aria-label={t('toc_drag_section_tooltip')}
          onDragStart={(e) => {
            if (!isDraggable || isMobile) return;
            onDragStartIndex(flatIndex);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onTouchStart={(e) => {
            if (!isDraggable || !isMobile) return;
            e.stopPropagation();
            onTouchStartIndex(flatIndex, e.touches[0].clientY);
          }}
        >
          <svg width="16" height="16" style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="5" r="1" fill="currentColor" />
            <circle cx="9" cy="12" r="1" fill="currentColor" />
            <circle cx="9" cy="19" r="1" fill="currentColor" />
            <circle cx="15" cy="5" r="1" fill="currentColor" />
            <circle cx="15" cy="12" r="1" fill="currentColor" />
            <circle cx="15" cy="19" r="1" fill="currentColor" />
          </svg>
        </span>
      </ButtonTooltip>
    );
  };

  const panelWidth = `${pinnedWidthPx}px`;
  const mobilePanelMaxHeight = isFocusMode
    ? 'calc(100dvh - 2rem)'
    : 'calc(100dvh - 64px - 2rem)';
  const desktopFloatingMaxHeight = isFocusMode
    ? 'calc(100dvh - 2rem)'
    : 'calc(100vh - 64px - 1rem)';
  const showPinClearance = isDesktopFloating && isFloatingTocVisible;

  const closeToc = (): void => {
    setIsTocPinned(false);
    setIsFloatingTocVisible(false);
  };

  const overlayPanelStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        top: floatingEditorControlTop(isFocusMode),
        left: FLOATING_EDITOR_CONTROL_MOBILE_LEFT,
        width: panelWidth,
        maxHeight: mobilePanelMaxHeight,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        borderRadius: '8px',
        transformOrigin: 'top left',
        transform: isOverlayVisible ? 'scale(1)' : 'scale(0)',
        opacity: isOverlayVisible ? 1 : 0,
        transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
        zIndex: 40,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: isOverlayVisible ? 'auto' : 'none',
      }
    : {
        position: 'fixed',
        top: floatingEditorControlTop(isFocusMode),
        left: desktopFloatingLeft,
        width: panelWidth,
        maxHeight: desktopFloatingMaxHeight,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        borderRadius: '8px',
        transformOrigin: 'top left',
        transform: isOverlayVisible ? 'scale(1)' : 'scale(0)',
        opacity: isOverlayVisible ? 1 : 0,
        transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
        zIndex: 40,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: isOverlayVisible ? 'auto' : 'none',
      };

  const pinnedPanelStyle: React.CSSProperties = {
    width: isDesktopPinned ? panelWidth : '0px',
    flexShrink: 0,
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    transition: isResizingPinnedWidth ? 'none' : 'width 0.2s ease-out',
  };

  return (
    <>
      {isMobile && (isFloatingTocVisible || isTocPinned) && (
        <div
          onClick={() => setIsFloatingTocVisible(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 39,
            transition: 'opacity 0.3s ease-in-out',
          }}
        />
      )}
      <div
        ref={isDesktopPinned ? pinnedPanelRef : undefined}
        onMouseEnter={() => {
          if (isDesktopFloating) {
            setIsFloatingTocVisible(true);
          }
        }}
        onMouseLeave={(event) => {
          if (!isDesktopFloating || draggedTocIndex !== null || touchDraggedIndex !== null) {
            return;
          }
          const relatedTarget = event.relatedTarget;
          if (
            relatedTarget instanceof Element &&
            relatedTarget.closest('[data-floating-toc-button="true"]')
          ) {
            return;
          }
          setIsFloatingTocVisible(false);
        }}
        style={isDesktopPinned ? pinnedPanelStyle : overlayPanelStyle}
        className={isDesktopPinned ? '' : 'bg-card text-card-foreground border border-border shadow-lg'}
        data-toc-mode={isDesktopPinned ? 'pinned' : 'floating'}
      >
        {isDesktopPinned && (
          <TooltipProvider delayDuration={0}>
            <ButtonTooltip tooltip={t('close_table_of_contents_tooltip')} side="left">
              <button
                type="button"
                onClick={closeToc}
                data-toc-close-button="true"
                className="floating-editor-control group absolute top-2 right-2 z-[2] flex cursor-pointer items-center justify-center border-0 p-1.5 text-muted-foreground"
                aria-label={t('close_table_of_contents')}
              >
                <X className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90" />
              </button>
            </ButtonTooltip>
          </TooltipProvider>
        )}
        <div
          ref={tocScrollContainerRef}
          style={{
            width: panelWidth,
            height: isDesktopPinned ? '100%' : undefined,
            flex: isDesktopPinned ? undefined : 1,
            minHeight: isDesktopPinned ? undefined : 0,
            maxHeight: isMobile
              ? mobilePanelMaxHeight
              : isDesktopPinned
                ? '100%'
                : desktopFloatingMaxHeight,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            padding: showPinClearance ? '48px 0 16px' : '16px 0',
            boxSizing: 'border-box',
          }}
          className={(isDesktopPinned ? 'bg-card text-card-foreground border-r border-border' : 'bg-card text-card-foreground') + ' toc-scroll-container'}
          data-toc-pin-clearance={showPinClearance ? 'true' : undefined}
        >
        <TooltipProvider delayDuration={0}>
        <div>
        {tocHeadings.length === 0 && tocParagraphs.length === 0 ? (
          <div
            data-toc-empty-state="true"
            className="flex flex-col gap-3 px-4 pb-4 pt-10 text-center text-sm text-muted-foreground"
          >
            <p className="m-0">{t('no_headers_toc_cant_be_created')}</p>
            <p className="m-0">{t('no_headers_toc_how_to_add')}</p>
          </div>
        ) : tocHeadings.length === 0 && tocParagraphs.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {[...tocParagraphs].sort((a, b) => a.pos - b.pos).map((paragraph, pIdx) => {
              const paragraphFlatIndex = getFlatIndex(paragraph.pos, 'paragraph');
              const isDraggable = !isReadOnly;
              const isDragging = draggedTocIndex === paragraphFlatIndex || touchDraggedIndex === paragraphFlatIndex;
              const isDragOver = (dragOverTocIndex === paragraphFlatIndex && draggedTocIndex !== null && draggedTocIndex !== paragraphFlatIndex) ||
                (touchDragOverIndex === paragraphFlatIndex && touchDraggedIndex !== null && touchDraggedIndex !== paragraphFlatIndex);

              const isActiveParagraph = activeTocPos === paragraph.pos;

              return (
                <div
                  key={`paragraph-${paragraph.pos}-${pIdx}`}
                  data-toc-item-pos={paragraph.pos}
                  data-active={isActiveParagraph || undefined}
                  className="group rounded-md transition-colors hover:bg-accent"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    borderRadius: '0.375rem',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    marginLeft: '8px',
                    marginRight: '8px',
                    opacity: isDragging ? 0.5 : 1,
                    border: isDragOver ? '2px dashed hsl(var(--primary))' : '2px dashed transparent',
                    transition: 'all 0.2s',
                    ...(isActiveParagraph ? { backgroundColor: ACTIVE_TOC_ITEM_STYLE.backgroundColor } : {}),
                  }}
                  onDragOver={(e) => {
                    if (draggedTocIndex === null || isMobile || !setDragOverTocIndex) return;
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverTocIndex(paragraphFlatIndex);
                  }}
                  onDragLeave={() => {
                    if (isMobile || !setDragOverTocIndex) return;
                    setDragOverTocIndex(null);
                  }}
                  onDrop={(e) => {
                    if (isMobile || !moveTocSection || !setDraggedTocIndex || !setDragOverTocIndex) return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (draggedTocIndex !== null && draggedTocIndex !== paragraphFlatIndex) {
                      moveTocSection(draggedTocIndex, paragraphFlatIndex);
                    }
                    setDraggedTocIndex(null);
                    setDragOverTocIndex(null);
                  }}
                  onDragEnd={() => {
                    if (isMobile || !setDraggedTocIndex || !setDragOverTocIndex) return;
                    setDraggedTocIndex(null);
                    setDragOverTocIndex(null);
                  }}
                  onTouchEnd={(e) => {
                    if (touchDraggedIndex === null || !moveTocSection || !setTouchDraggedIndex || !setTouchDragOverIndex || !setTouchStartY) return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (touchDragOverIndex !== null && touchDraggedIndex !== touchDragOverIndex) {
                      moveTocSection(touchDraggedIndex, touchDragOverIndex);
                    }
                    setTouchDraggedIndex(null);
                    setTouchDragOverIndex(null);
                    setTouchStartY(null);
                  }}
                  onTouchCancel={() => {
                    if (!setTouchDraggedIndex || !setTouchDragOverIndex || !setTouchStartY) return;
                    setTouchDraggedIndex(null);
                    setTouchDragOverIndex(null);
                    setTouchStartY(null);
                  }}
                >
                  <button
                    onClick={() => {
                      if (touchDraggedIndex !== null || !scrollToTocPosition) return;
                      scrollToTocPosition(paragraph.pos);
                      if (isMobile) {
                        setIsFloatingTocVisible(false);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'left',
                      background: 'transparent',
                      cursor: 'pointer',
                      borderRadius: '0.375rem',
                      fontSize: '0.8rem',
                      lineHeight: 1,
                      padding: '0.25rem',
                      color: isActiveParagraph ? ACTIVE_TOC_ITEM_STYLE.color : 'var(--muted-foreground)',
                      fontWeight: isActiveParagraph ? ACTIVE_TOC_ITEM_STYLE.fontWeight : undefined,
                      transition: 'background-color 0.2s',
                    }}
                    title={paragraph.text}
                  >
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '100%', lineHeight: 1.25 }}>
                      {paragraph.text || t('untitled')}
                    </span>
                  </button>
                  {isMobile ? (
                    renderDragHandle(
                      paragraphFlatIndex,
                      isDraggable,
                      (index) => setDraggedTocIndex(index),
                      (index, y) => { setTouchDraggedIndex(index); setTouchStartY(y); }
                    )
                  ) : (
                    <div className="flex items-center" style={{ position: 'absolute', right: '8px', top: 0, bottom: 0, backgroundColor: 'var(--accent)' }}>
                      {renderDragHandle(
                        paragraphFlatIndex,
                        isDraggable,
                        (index) => setDraggedTocIndex(index),
                        (index, y) => { setTouchDraggedIndex(index); setTouchStartY(y); }
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          (() => {

          const renderParagraphs = (paragraphs: Array<{ text: string; pos: number }>, indent: string) => (
            <div style={{ paddingLeft: indent }}>
              {[...paragraphs].sort((a, b) => a.pos - b.pos).map((paragraph, pIdx) => {
                const paragraphFlatIndex = getFlatIndex(paragraph.pos, 'paragraph');
                const isParagraphDraggable = !isReadOnly;
                const isParagraphDragging = draggedTocIndex === paragraphFlatIndex || touchDraggedIndex === paragraphFlatIndex;
                const isParagraphDragOver = (dragOverTocIndex === paragraphFlatIndex && draggedTocIndex !== null && draggedTocIndex !== paragraphFlatIndex) ||
                  (touchDragOverIndex === paragraphFlatIndex && touchDraggedIndex !== null && touchDraggedIndex !== paragraphFlatIndex);
                const isActiveParagraph = activeTocPos === paragraph.pos;

                return (
                  <div
                    key={`paragraph-${paragraph.pos}-${pIdx}`}
                    data-toc-item-pos={paragraph.pos}
                    data-active={isActiveParagraph || undefined}
                    className="group rounded-md transition-colors hover:bg-accent"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      borderRadius: '0.375rem',
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      marginLeft: '8px',
                      marginRight: '8px',
                      opacity: isParagraphDragging ? 0.5 : 1,
                      border: isParagraphDragOver ? '2px dashed hsl(var(--primary))' : '2px dashed transparent',
                      transition: 'all 0.2s',
                      ...(isActiveParagraph ? { backgroundColor: ACTIVE_TOC_ITEM_STYLE.backgroundColor } : {}),
                    }}
                    onDragOver={(e) => {
                      if (draggedTocIndex === null || isMobile || !setDragOverTocIndex) return;
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverTocIndex(paragraphFlatIndex);
                    }}
                    onDragLeave={() => {
                      if (isMobile || !setDragOverTocIndex) return;
                      setDragOverTocIndex(null);
                    }}
                    onDrop={(e) => {
                      if (isMobile || !moveTocSection || !setDraggedTocIndex || !setDragOverTocIndex) return;
                      e.preventDefault();
                      e.stopPropagation();
                      if (draggedTocIndex !== null && draggedTocIndex !== paragraphFlatIndex) {
                        moveTocSection(draggedTocIndex, paragraphFlatIndex);
                      }
                      setDraggedTocIndex(null);
                      setDragOverTocIndex(null);
                    }}
                    onDragEnd={() => {
                      if (isMobile || !setDraggedTocIndex || !setDragOverTocIndex) return;
                      setDraggedTocIndex(null);
                      setDragOverTocIndex(null);
                    }}
                    onTouchEnd={(e) => {
                      if (touchDraggedIndex === null || !moveTocSection || !setTouchDraggedIndex || !setTouchDragOverIndex || !setTouchStartY) return;
                      e.preventDefault();
                      e.stopPropagation();
                      if (touchDragOverIndex !== null && touchDraggedIndex !== touchDragOverIndex) {
                        moveTocSection(touchDraggedIndex, touchDragOverIndex);
                      }
                      setTouchDraggedIndex(null);
                      setTouchDragOverIndex(null);
                      setTouchStartY(null);
                    }}
                    onTouchCancel={() => {
                      if (!setTouchDraggedIndex || !setTouchDragOverIndex || !setTouchStartY) return;
                      setTouchDraggedIndex(null);
                      setTouchDragOverIndex(null);
                      setTouchStartY(null);
                    }}
                  >
                    <button
                      onClick={() => {
                        if (touchDraggedIndex !== null || !scrollToTocPosition) return;
                        scrollToTocPosition(paragraph.pos);
                        if (isMobile) {
                          setIsFloatingTocVisible(false);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left',
                        background: 'transparent',
                        cursor: 'pointer',
                        borderRadius: '0.375rem',
                        fontSize: '0.8rem',
                        lineHeight: 1,
                        padding: '0.25rem',
                        color: isActiveParagraph ? ACTIVE_TOC_ITEM_STYLE.color : 'var(--muted-foreground)',
                        fontWeight: isActiveParagraph ? ACTIVE_TOC_ITEM_STYLE.fontWeight : undefined,
                        transition: 'background-color 0.2s',
                      }}
                      title={paragraph.text}
                    >
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '100%', lineHeight: 1.25 }}>
                        {paragraph.text || t('untitled')}
                      </span>
                    </button>
                    {isMobile ? (
                      renderDragHandle(
                        paragraphFlatIndex,
                        isParagraphDraggable,
                        (index) => setDraggedTocIndex(index),
                        (index, y) => { setTouchDraggedIndex(index); setTouchStartY(y); }
                      )
                    ) : (
                      <div className="flex items-center" style={{ position: 'absolute', right: '8px', top: 0, bottom: 0, backgroundColor: 'var(--accent)' }}>
                        {renderDragHandle(
                          paragraphFlatIndex,
                          isParagraphDraggable,
                          (index) => setDraggedTocIndex(index),
                          (index, y) => { setTouchDraggedIndex(index); setTouchStartY(y); }
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );

          const activeDraggedIndex = draggedTocIndex !== null ? draggedTocIndex : touchDraggedIndex;
          const activeDragOverIndex = dragOverTocIndex !== null ? dragOverTocIndex : touchDragOverIndex;
          const draggedLevel = activeDraggedIndex !== null ? (combinedSorted[activeDraggedIndex]?.level ?? null) : null;
          const draggedParentFlatIndex = activeDraggedIndex !== null ? findWouldBeParentFlatIndex(activeDraggedIndex - 1, draggedLevel) : null;
          const dragOverTargetParentFlatIndex = activeDragOverIndex !== null ? findWouldBeParentFlatIndex(activeDragOverIndex, draggedLevel) : null;
          const isReparentingDrag = activeDraggedIndex !== null && activeDragOverIndex !== null && activeDraggedIndex !== activeDragOverIndex &&
            dragOverTargetParentFlatIndex !== draggedParentFlatIndex;

          const renderNode = (node: TocNode): React.ReactNode => {
            const itemId = tocItemId(node.originalIndex);
            const headingIndent = `${Math.max(0, node.heading.level - 1) * nestingIndentRem}rem`;
            const paragraphIndent = `${Math.max(0, node.heading.level) * nestingIndentRem}rem`;
            const hasHeadingChildren = node.children.length > 0;
            const hasParagraphs = node.paragraphs.length > 0;
            const isExpandable = hasHeadingChildren || hasParagraphs;
            const isDraggable = !isReadOnly;
            const flatIndex = getFlatIndex(node.heading.pos, 'heading');
            const isDragging = draggedTocIndex === flatIndex || touchDraggedIndex === flatIndex;
            const isDragOver = (dragOverTocIndex === flatIndex && draggedTocIndex !== null && draggedTocIndex !== flatIndex) ||
              (touchDragOverIndex === flatIndex && touchDraggedIndex !== null && touchDraggedIndex !== flatIndex);
            const isDropParentTarget = isReparentingDrag && flatIndex === dragOverTargetParentFlatIndex;

            const rowStyle: React.CSSProperties = {
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              opacity: isDragging ? 0.5 : 1,
              border: (isDragOver || isDropParentTarget) ? '2px dashed hsl(var(--primary))' : '2px dashed transparent',
              transition: 'all 0.2s',
              paddingLeft: `calc(${headingIndent} + 8px)`,
              paddingRight: '8px',
              marginLeft: '8px',
              marginRight: '8px',
            };

            const rowDragProps = {
              onDragOver: (e: React.DragEvent) => {
                if (draggedTocIndex === null || isMobile || !setDragOverTocIndex) return;
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'move';
                setDragOverTocIndex(flatIndex);
              },
              onDragLeave: () => {
                if (isMobile || !setDragOverTocIndex) return;
                setDragOverTocIndex(null);
              },
              onDrop: (e: React.DragEvent) => {
                if (isMobile || !moveTocSection || !setDraggedTocIndex || !setDragOverTocIndex) return;
                e.preventDefault();
                e.stopPropagation();
                if (draggedTocIndex !== null && draggedTocIndex !== flatIndex) {
                  moveTocSection(draggedTocIndex, flatIndex);
                }
                setDraggedTocIndex(null);
                setDragOverTocIndex(null);
              },
              onDragEnd: () => {
                if (isMobile || !setDraggedTocIndex || !setDragOverTocIndex) return;
                setDraggedTocIndex(null);
                setDragOverTocIndex(null);
              },
              onTouchEnd: (e: React.TouchEvent) => {
                if (touchDraggedIndex === null || !moveTocSection || !setTouchDraggedIndex || !setTouchDragOverIndex || !setTouchStartY) return;
                e.preventDefault();
                e.stopPropagation();
                if (touchDragOverIndex !== null && touchDraggedIndex !== touchDragOverIndex) {
                  moveTocSection(touchDraggedIndex, touchDragOverIndex);
                }
                setTouchDraggedIndex(null);
                setTouchDragOverIndex(null);
                setTouchStartY(null);
              },
              onTouchCancel: () => {
                if (!setTouchDraggedIndex || !setTouchDragOverIndex || !setTouchStartY) return;
                setTouchDraggedIndex(null);
                setTouchDragOverIndex(null);
                setTouchStartY(null);
              },
            };

            const isTopLevel = node.heading.level === 1;
            const isActiveHeading = activeTocPos === node.heading.pos;
            const isAncestorHeading = ancestorHeadingPositions.has(node.heading.pos);
            const usesActiveTextColor = isActiveHeading || isAncestorHeading;

            const headingButton = (
              <button
                type="button"
                data-toc-item-pos={node.heading.pos}
                data-active={isActiveHeading || undefined}
                data-toc-ancestor={isAncestorHeading || undefined}
                onClick={() => {
                  if (touchDraggedIndex !== null || !scrollToTocPosition) return;
                  scrollToTocPosition(node.heading.pos);
                  if (isMobile) {
                    setIsFloatingTocVisible(false);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                  fontSize: '0.875rem',
                  lineHeight: 1,
                  fontWeight: usesActiveTextColor
                    ? ACTIVE_TOC_ITEM_STYLE.fontWeight
                    : (isTopLevel ? 500 : 400),
                  color: usesActiveTextColor
                    ? ACTIVE_TOC_ANCESTOR_TEXT_STYLE.color
                    : (node.heading.level <= 2 ? 'var(--foreground)' : 'var(--muted-foreground)'),
                  padding: '0.5rem 0.25rem',
                  textAlign: 'left',
                  background: isActiveHeading ? ACTIVE_TOC_ITEM_STYLE.backgroundColor : 'transparent',
                  cursor: 'pointer',
                  borderRadius: '0.375rem',
                  transition: 'background-color 0.2s',
                }}
                title={node.heading.text}
              >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '100%', textAlign: 'left', lineHeight: 1.25 }}>
                  {node.heading.text || t('untitled')}
                </span>
              </button>
            );

            const dragHandle = renderDragHandle(
              flatIndex,
              isDraggable,
              (index) => setDraggedTocIndex(index),
              (index, y) => { setTouchDraggedIndex(index); setTouchStartY(y); }
            );

            const overlayControls = (controls: React.ReactNode) =>
              isMobile ? controls : (
                <div className="flex items-center" style={{ position: 'absolute', right: '8px', top: 0, bottom: 0, backgroundColor: 'var(--accent)' }}>
                  {controls}
                </div>
              );

            if (!isExpandable) {
              return (
                <div key={itemId} className="group rounded-md transition-colors hover:bg-accent" style={rowStyle} {...rowDragProps}>
                  {headingButton}
                  {overlayControls(dragHandle)}
                </div>
              );
            }

            const headingChildren = hasHeadingChildren
              ? node.children.map((child) => renderNode(child))
              : null;

            const isSectionExpanded = openIds.includes(itemId);
            const sectionToggleTooltip = isSectionExpanded
              ? t('toc_collapse_section_tooltip')
              : t('toc_expand_section_tooltip');

            return (
              <div key={itemId} className="w-full">
                <Accordion
                  type="multiple"
                  className="w-full"
                  value={isSectionExpanded ? [itemId] : []}
                  onValueChange={(values) => setTocItemOpen(itemId, values.includes(itemId))}
                >
                  <AccordionItem value={itemId} className="border-none">
                    <AccordionPrimitive.Header className="flex items-center group rounded-md transition-colors hover:bg-accent" style={rowStyle} {...rowDragProps}>
                      {headingButton}
                      {overlayControls(
                        <>
                          <ButtonTooltip tooltip={sectionToggleTooltip} side="left" hidden={isMobile}>
                            <AccordionPrimitive.Trigger
                              className={(isMobile ? 'flex' : 'hidden group-hover:flex') + ' shrink-0 items-center justify-center p-2 rounded-md transition-[transform,color] duration-200 hover:bg-accent hover:text-foreground data-[state=open]:rotate-180'}
                              style={{ marginLeft: '0.25rem' }}
                              aria-label={sectionToggleTooltip}
                            >
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            </AccordionPrimitive.Trigger>
                          </ButtonTooltip>
                          {dragHandle}
                        </>
                      )}
                    </AccordionPrimitive.Header>
                    <AccordionContent className="pb-0">
                      {hasParagraphs && renderParagraphs(node.paragraphs, paragraphIndent)}
                      {!hasParagraphs && headingChildren}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                {hasParagraphs && headingChildren}
              </div>
            );
          };

          return (
            <div className="w-full" style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
              {tree.map((node) => renderNode(node))}
            </div>
          );
        })()
        )}
        </div>
        </TooltipProvider>
        </div>
        {isDesktopPinned && (
          <div
            data-toc-resize-handle="true"
            className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize z-10 flex items-center justify-center group"
            onMouseDown={startPinnedWidthResize}
            role="separator"
            aria-orientation="vertical"
            aria-label={t('resize_table_of_contents')}
          >
            <div className="flex flex-col items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="h-4 w-4 text-foreground opacity-40" />
              <GripVertical className="h-4 w-4 text-foreground opacity-40" />
              <GripVertical className="h-4 w-4 text-foreground opacity-40" />
            </div>
            <div className="absolute inset-0 bg-transparent group-hover:bg-sidebar-accent/30 transition-colors" />
          </div>
        )}
      </div>
    </>
  );
});

FloatingToc.displayName = 'FloatingToc';

